import React from "react";
import { createPortal } from "react-dom";
import { isInteractive } from "@v2/mobile/MobileDrawer/helpers";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import { cn } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";

const ANIM_MS = 220;

const classes = {
  Wrapper: "fixed inset-0 z-modal",
  Backdrop: "absolute inset-0 w-full h-full",
  Container:
    "absolute inset-x-0 bottom-0 flex justify-center pb-[env(safe-area-inset-bottom)] shadow-2xl",
  Inner: "w-full max-w-md space-y-2",
  InnerMd: "w-full max-w-[1000px] space-y-2",
  Panel:
    "rounded-t-2xl bg-white shadow-xl ring-1 ring-black/5 overflow-hidden dark:bg-neutral-900 pb-5",
  // Full-width sticky header = the drag handle area
  GrabArea:
    "sticky top-0 z-10 select-none bg-white dark:bg-neutral-900 cursor-grab active:cursor-grabbing",
  GrabAreaInner: "py-3 min-h-[44px] flex items-center justify-center",
  Grabber: "h-1 w-[60px] rounded-full bg-black dark:bg-neutral-700",
  ContentScroller: "overflow-y-auto overscroll-contain relative",
  SecondCard:
    "overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900",
  FooterContent: "bg-white"
};

function isInDragHandle(el: Element | null): boolean {
  return !!(el && (el as HTMLElement).closest?.("[data-drag-handle]"));
}

export interface MobileDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVisibleChange?: (isVisible: boolean, isOpen: boolean) => void;
  size?: "fit" | "full";
  revealGap?: number;
  dismissible?: boolean;
  dragCloseThreshold?: number;
  className?: string;
  ariaLabel?: string;
  children: React.ReactNode;
  below?: React.ReactNode;
  portalContainer?: Element | null;
  dragOnTileOnly?: boolean;
  scrollableContent?: boolean;
  contentUnderGrabber?: boolean;
  showBackdrop?: boolean;
  topContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  containerClassName?: string;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onOpenChange,
  onVisibleChange,
  size = "fit",
  revealGap = 120,
  dismissible = true,
  dragCloseThreshold = 20,
  className,
  ariaLabel = "Drawer",
  children,
  below,
  portalContainer,
  dragOnTileOnly = false,
  scrollableContent = false,
  contentUnderGrabber = false,
  showBackdrop = true,
  topContent,
  footerContent,
  containerClassName
}) => {
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const panelRef = React.useRef<HTMLDivElement>(null);
  const grabAreaRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Force tile-only drag when scrollable, to avoid scroll/drag conflicts
  const effectiveDragOnTileOnly = dragOnTileOnly || scrollableContent;

  const { isVisible } = useCloseAnimationTimeout(isOpen, {
    defaultState: false,
    animationDuration: ANIM_MS
  });

  const [panelHeight, setPanelHeight] = React.useState(0);
  const [animY, setAnimY] = React.useState(0);
  const [dragY, setDragY] = React.useState(0);
  const [transitionOn, setTransitionOn] = React.useState(true);

  const dragging = React.useRef(false);
  const dragStartY = React.useRef(0);

  React.useLayoutEffect(() => {
    if (!isVisible) return;
    const measure = () => {
      const h = panelRef.current?.offsetHeight ?? 0;
      if (h && h !== panelHeight) setPanelHeight(h);
    };
    measure();
    const r = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(r);
  }, [isVisible, size, children, panelHeight]);

  React.useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  React.useLayoutEffect(() => {
    if (!isVisible) return;
    if (isOpen) {
      const start =
        (panelRef.current?.offsetHeight ??
          (panelHeight || window.innerHeight)) + 20;
      setTransitionOn(false);
      setAnimY(start);
      setDragY(0);
      requestAnimationFrame(() => {
        setTransitionOn(true);
        setAnimY(0);
      });
    } else {
      const end =
        (panelRef.current?.offsetHeight ??
          (panelHeight || window.innerHeight)) + 20;
      setTransitionOn(true);
      setDragY(0);
      setAnimY(end);
    }
  }, [isVisible, isOpen]);

  React.useEffect(() => {
    if (!isVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissible) onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isVisible, dismissible, onOpenChange]);

  // Pointer handlers — attach to grab area or whole panel
  React.useEffect(() => {
    if (!isVisible) return;

    const target: HTMLElement | null = effectiveDragOnTileOnly
      ? grabAreaRef.current
      : panelRef.current;

    if (!target) return;

    const onPointerDown = (e: PointerEvent) => {
      const targetEl = e.target as Element;

      // If drag is allowed only from the tile, we don't need this guard
      if (!effectiveDragOnTileOnly) {
        const onHandle = isInDragHandle(targetEl);
        const interactive = isInteractive(targetEl);

        // Skip only when it's interactive AND not the handle
        if (interactive && !onHandle) return;
      }

      dragging.current = true;
      dragStartY.current = e.clientY;
      setTransitionOn(false);

      // prevent scroller kick-in when starting on the header
      if (isInDragHandle(targetEl)) e.preventDefault();

      try {
        target.setPointerCapture(e.pointerId);
      } catch {}
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      const dy = e.clientY - dragStartY.current;
      setDragY(Math.max(0, dy));
    };

    const endDrag = () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (dragY > (dragCloseThreshold ?? 120)) {
        setTransitionOn(true);
        const end =
          (panelRef.current?.offsetHeight ??
            (panelHeight || window.innerHeight)) + 20;
        setAnimY(end);
        setDragY(0);
        onOpenChange(false);
      } else {
        setTransitionOn(true);
        setDragY(0);
      }
    };

    target.addEventListener("pointerdown", onPointerDown);
    target.addEventListener("pointermove", onPointerMove);
    target.addEventListener("pointerup", endDrag);
    target.addEventListener("pointercancel", endDrag);

    return () => {
      target.removeEventListener("pointerdown", onPointerDown);
      target.removeEventListener("pointermove", onPointerMove);
      target.removeEventListener("pointerup", endDrag);
      target.removeEventListener("pointercancel", endDrag);
    };
  }, [
    dragY,
    dragCloseThreshold,
    panelHeight,
    onOpenChange,
    isVisible,
    effectiveDragOnTileOnly
  ]);

  React.useEffect(() => {
    onVisibleChange?.(isVisible, isOpen);
  }, [isVisible, isOpen]);

  if (!isVisible) return null;

  const root =
    portalContainer ?? (typeof document !== "undefined" ? document.body : null);

  const gap = Math.max(revealGap, 0);
  const maxHeight = `calc(100dvh - ${gap}px)`;
  const height = size === "full" ? maxHeight : "auto";

  const translateY = animY + dragY;

  const backDropMax = 0.4;
  const denom = (panelHeight || window.innerHeight) + 20;
  const backdropOpacity = backDropMax * (1 - Math.min(translateY / denom, 1));

  const transition = transitionOn
    ? `transform ${ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
    : "none";
  const backdropTransition = transitionOn
    ? `opacity ${ANIM_MS}ms ease`
    : "none";

  // Panel (animated shell) must not scroll
  const panelCommonStyle: React.CSSProperties = {
    transform: `translateY(${translateY}px)`,
    transition,
    maxHeight,
    height,
    overflow: "hidden",
    touchAction: "none",
    willChange: "transform"
  };

  // Content area scrolls independently
  const scrollerStyle: React.CSSProperties = {
    maxHeight: "inherit",
    WebkitOverflowScrolling: "touch",
    overflowY: scrollableContent ? "auto" : "hidden",
    touchAction: scrollableContent ? "pan-y" : "none"
  };

  const belowStyle: React.CSSProperties = {
    transform: `translateY(${translateY}px)`,
    transition,
    willChange: "transform"
  };

  const sheet = (
    <div
      className={showBackdrop ? classes.Wrapper : undefined}
      aria-modal="true"
      aria-label={ariaLabel}
    >
      {showBackdrop && (
        <button
          aria-label="Close"
          className={classes.Backdrop}
          style={{
            backgroundColor: "rgba(0,0,0,0.40)",
            opacity: backdropOpacity,
            transition: backdropTransition
          }}
          onClick={() => dismissible && onOpenChange(false)}
        />
      )}

      <div className={classes.Container}>
        <div
          className={[
            isMd ? classes.InnerMd : classes.Inner,
            contentUnderGrabber ? "space-y-0" : "space-y-2"
          ].join(" ")}
        >
          <div
            ref={panelRef}
            className={[
              classes.Panel,
              contentUnderGrabber ? "relative" : "",
              className || ""
            ].join(" ")}
            style={panelCommonStyle}
          >
            {/* ABSOLUTE, FULL-WIDTH GRAB HANDLE OVERLAY */}
            <div
              ref={grabAreaRef}
              className={
                contentUnderGrabber
                  ? // overlay on top of content
                    "absolute inset-x-0 top-0 z-10 cursor-grab select-none active:cursor-grabbing dark:bg-neutral-900"
                  : // original sticky behavior
                    classes.GrabArea
              }
              aria-hidden
              aria-label="Drag to close"
              data-drag-handle=""
            >
              <div className={classes.GrabAreaInner} data-drag-handle="">
                <div className={classes.Grabber} data-drag-handle="" />
              </div>
            </div>
            {topContent && <div>{topContent}</div>}
            {/* Scrollable content lives UNDER the overlay when contentUnderGrabber = true */}
            <div
              ref={contentRef}
              className={cn(classes.ContentScroller, containerClassName)}
              style={scrollerStyle} // no extra padding-top: the grabber overlays the content
            >
              {children}
            </div>
            {below ? (
              <div className={cn("px-6")} style={belowStyle}>
                {below}
              </div>
            ) : null}
          </div>

          {footerContent && (
            <div className={classes.FooterContent}>{footerContent}</div>
          )}
        </div>
      </div>
    </div>
  );

  return root ? createPortal(sheet, root) : sheet;
};
